import { ServerSideEventService } from './serverSideEvent.service';
import { EventEmitter, Injectable, OnDestroy } from '@angular/core';
import { Observable, Subject, Subscription, take, takeUntil, lastValueFrom } from 'rxjs';
import { HmiService } from './hmi.service';
import { ChannelFlags } from 'ops/common/models/device-flags';
import { SystemStatusService } from './system-status.service';
import { Channel, FlagOption, ChannelFns } from 'ops/common/models/channel';
import { ChannelHistoryValuesService } from './channel-history-values.service';
import { ChannelService } from './channel.service';
import { AlarmStatusCount } from '@models/alarm';
import { ChannelSocketService } from './channel-socket.service';
import { AlarmLevel } from 'ops/common/models/alarm-level';

@Injectable()
export class DeviceFlagsService implements OnDestroy {
    private onFlagsUpdated$ = new Subject<ChannelFlags>();
    private onAllFlagsLoaded$ = new EventEmitter<void>();
    private onAllFlagsUpdated$ = new EventEmitter<void>();
    private onAlarmStatusCountUpdated$ = new EventEmitter<AlarmStatusCount>();

    private flagValues: { [key: string]: ChannelFlags } = {};
    private channelStatus: { [key: string]: FlagOption } = {};

    private channelLevel: { [key: string]: AlarmLevel } = {};
    private groupChannelLevel: { [key: string]: {} } = {};
    private channelsLevels: { [key: string]: {} | string } = {};
    private alarmStatusCount: AlarmStatusCount;

    private failOverStates = {};
    private failUnderStates = {};
    private activeFrequency = 2000;
    private simulatorActive = false;

    private updateChannelsInterval = 1000;

    private serverEventSubscription: Subscription;
    private destroySubscriptions$: Subject<void> = new Subject();

    private updateInterval: NodeJS.Timeout = null;

    constructor(
        private hmiService: HmiService,
        private serverSideEvent: ServerSideEventService,
        private channelHistoryValues: ChannelHistoryValuesService,
        private channelService: ChannelService,
        private channelSocketService: ChannelSocketService,
    ) {
        this.alarmStatusCount = {
            [AlarmLevel.Alarm]: 0,
            [AlarmLevel.Caution]: 0,
            [AlarmLevel.Warning]: 0,
        };

        this.channelService
            .getChannels()
            .pipe(take(1))
            .subscribe((channels) => {
                this.updateChannelsLevels(channels);
            });

        this.hmiService
            .onFrequencyValue()
            .pipe(takeUntil(this.destroySubscriptions$))
            .subscribe((frequency: number) => {
                let previousFrequency = this.activeFrequency;
                this.activeFrequency = frequency;
                if (this.simulatorActive && frequency !== previousFrequency) {
                    this.stopRefreshing();
                    this.startRefresing();
                }
            });

        this.hmiService
            .onSimulatorStart()
            .pipe(takeUntil(this.destroySubscriptions$))
            .subscribe(async (active: boolean) => {
                this.simulatorActive = active;
                if (active) {
                    this.startRefresing();
                } else {
                    this.stopRefreshing();
                }
            });

        this.channelSocketService
            .updateListSubject()
            .pipe(takeUntil(this.destroySubscriptions$))
            .subscribe(async () => {
                let channels = await lastValueFrom(this.channelService.getChannels());
                this.updateChannelsLevels(channels);
            });

        this.loadActiveFrequency();
        this.loadDeviceValues();
        this.loadSimulatorActive();
    }

    ngOnDestroy(): void {
        this.destroySubscriptions$.next();
        this.destroySubscriptions$.complete();

        this.onAllFlagsLoaded$.complete();
        this.onFlagsUpdated$.complete();
        this.stopRefreshing();
    }

    startRefresing() {
        if (this.serverEventSubscription) {
            return;
        }

        this.serverSideEvent.startEvents();
        this.channelHistoryValues.startUpdates();
        this.serverEventSubscription = this.serverSideEvent.onChannelDataReceived.subscribe(
            (channelsData) => {
                const updates = {};
                for (let i = 0; i < channelsData.length; i++) {
                    updates[channelsData[i].id] = channelsData[i];
                    this.writeFailStates(channelsData[i]);
                    this.updateStatus(channelsData[i]);
                    this.emitFlags(channelsData[i]);
                }
                Object.assign(this.flagValues, updates);
                this.updateFlagsCounter();
                if (!this.updateInterval) {
                    this.startInterval();
                }
            },
        );
    }

    startInterval() {
        this.emitAllFlagsUpdated();
        this.updateInterval = setInterval(() => {
            this.emitAllFlagsUpdated();
        }, this.updateChannelsInterval);
    }

    stopRefreshing() {
        clearInterval(this.updateInterval);
        this.updateInterval = null;
        this.serverEventSubscription?.unsubscribe();
        this.channelHistoryValues.stopUpdates();
        this.serverEventSubscription = null;
        this.serverSideEvent.stopEvents();
    }

    private writeFailStates(channel: ChannelFlags) {
        this.failUnderStates[channel.id] =
            (channel.flags.alarm.under |
                channel.flags.cancelAuto1.under |
                channel.flags.cancelAuto2.under |
                channel.flags.cancelAuto3.under |
                channel.flags.cancelManual.under |
                channel.flags.unAck.under) !==
            0;

        this.failOverStates[channel.id] =
            (channel.flags.alarm.over |
                channel.flags.cancelAuto1.over |
                channel.flags.cancelAuto2.over |
                channel.flags.cancelAuto3.over |
                channel.flags.cancelManual.over |
                channel.flags.unAck.over) !==
            0;
    }

    private updateStatus(channel: ChannelFlags) {
        if (!channel || !channel.id) return;
        let flags = channel.flags;
        if (!flags?.alarm || !flags?.unAck) return;

        let alarmFlags = flags.alarm;
        let ackedFlags = flags.unAck;

        let activeFlag = ChannelFns.getAlarmClass(alarmFlags);

        if (activeFlag == FlagOption.HIGH_HIGH) {
            this.channelStatus[channel.id] = FlagOption.HIGH_HIGH;
        } else if (activeFlag == FlagOption.LOW_LOW) {
            this.channelStatus[channel.id] = FlagOption.LOW_LOW;
        } else if (activeFlag == FlagOption.LOW) {
            this.channelStatus[channel.id] = FlagOption.LOW;
        } else if (activeFlag == FlagOption.HIGH) {
            this.channelStatus[channel.id] = FlagOption.HIGH;
        } else if (activeFlag == FlagOption.FEEDBACK) {
            this.channelStatus[channel.id] = FlagOption.FEEDBACK;
        } else if (activeFlag == FlagOption.OVER) {
            this.channelStatus[channel.id] = FlagOption.OVER;
        } else if (activeFlag == FlagOption.UNDER) {
            this.channelStatus[channel.id] = FlagOption.UNDER;
        } else if (activeFlag == FlagOption.NORMAL) {
            let state = this.channelStatus[channel.id];
            if (state != FlagOption.NORMAL && ackedFlags[state] == 1) {
                return this.channelStatus[channel.id];
            }
            this.channelStatus[channel.id] = FlagOption.NORMAL;
        } else {
            this.channelStatus[channel.id] = null;
        }
        let status = this.channelStatus[channel.id];
        if (!status) return;
        let levels = this.channelsLevels[channel.id];
        if (!levels) return;
        let channelLevel = levels[status];
        this.channelLevel[channel.id] = channelLevel;
        if (!this.channelsLevels[channel.id]['groupId']) return;
        let groupId = this.channelsLevels[channel.id]['groupId'];
        if (!this.groupChannelLevel[groupId]) {
            this.groupChannelLevel[groupId] = {};
        }
        this.groupChannelLevel[groupId][channel.id] = channelLevel;
    }

    onFlagsUpdated(): Observable<ChannelFlags> {
        return this.onFlagsUpdated$;
    }

    onAllFlagsUpdated(): EventEmitter<void> {
        return this.onAllFlagsUpdated$;
    }

    onAlarmStatusCountUpdated(): EventEmitter<AlarmStatusCount> {
        return this.onAlarmStatusCountUpdated$;
    }

    getFlags(channel: Channel): ChannelFlags {
        return this.flagValues[channel.fcuData.fcuChannelId];
    }

    getStatus(channel: Channel): FlagOption {
        return this.channelStatus[channel.fcuData.fcuChannelId];
    }

    isFailedOver(channel: Channel) {
        return this.failOverStates[channel.fcuData.fcuChannelId] === true;
    }

    isFailedUnder(channel: Channel) {
        return this.failUnderStates[channel.fcuData.fcuChannelId] === true;
    }

    getLevelsByGroupId(groupId) {
        let alarmStatusCount = {
            [AlarmLevel.Alarm]: 0,
            [AlarmLevel.Caution]: 0,
            [AlarmLevel.Warning]: 0,
        };
        const groupLevels = this.groupChannelLevel[groupId];

        if (!groupLevels) return alarmStatusCount;

        for (const level of Object.values(groupLevels)) {
            if (level || level == 0) {
                alarmStatusCount[level as AlarmLevel] += 1;
            }
        }
        return alarmStatusCount;
    }

    private updateFlagsCounter() {
        this.alarmStatusCount = {
            [AlarmLevel.Alarm]: 0,
            [AlarmLevel.Caution]: 0,
            [AlarmLevel.Warning]: 0,
        };
        for (const level of Object.values(this.channelLevel)) {
            if (level || level == 0) {
                this.alarmStatusCount[level] += 1;
            }
        }

        this.onAlarmStatusCountUpdated$.emit(this.alarmStatusCount);
    }

    private emitFlags(v: ChannelFlags) {
        this.onFlagsUpdated$.next(v);
    }

    private emitAllFlagsUpdated() {
        this.onAllFlagsUpdated$.emit();
    }

    private loadDeviceValues() {
        this.hmiService.askDeviceValues();
    }

    private loadSimulatorActive() {
        this.hmiService.askSimulatorActive();
    }

    private loadActiveFrequency() {
        this.hmiService.askFrequency();
    }

    private updateChannelsLevels(channels: Channel[]) {
        if (!channels || channels.length == 0) {
            this.channelsLevels = {};
        }
        channels.map((channel) => {
            if (!channel?.fcuData?.fcuChannelId) return;
            this.channelsLevels[channel.fcuData.fcuChannelId] = {
                [FlagOption.HIGH_HIGH]: channel.fcuData?.alarmSettingVeryHigh?.alarmLevel,
                [FlagOption.HIGH]: channel.fcuData?.alarmSettingHigh?.alarmLevel,
                [FlagOption.LOW]: channel.fcuData?.alarmSettingLow?.alarmLevel,
                [FlagOption.LOW_LOW]: channel.fcuData?.alarmSettingVeryLow?.alarmLevel,
                [FlagOption.FEEDBACK]: channel.fcuData?.alarmSettingFeedbackError?.alarmLevel,
                groupId: channel.groupId,
            };
        });
    }
}
